import { inject, Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	GuardResult,
	MaybeAsync,
	Router,
} from '@angular/router';
import {
	authState,
	FIREBASE_AUTH,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { Subject, takeUntil } from 'rxjs';
import { GuestAuthService } from '../services';

@Injectable({ providedIn: 'root' })
export class RedirectIfGuestAuthenticatedGuard implements CanActivate {
	private readonly auth = inject(FIREBASE_AUTH);
	private readonly guestAuthService = inject(GuestAuthService);
	private readonly router = inject(Router);
	private readonly organizationId$ = inject(ORGANIZATION_ID);

	/**
	 * Use on the auth (login) route. If the user is already authenticated with a
	 * valid guest session, redirects to the success route (e.g. /home) and
	 * prevents activation of the auth page.
	 */
	canActivate(route: ActivatedRouteSnapshot): MaybeAsync<GuardResult> {
		const successRoute =
			(route.data['companionSuccessRoute'] as string) ?? '/home';
		const resolved$ = new Subject<boolean>();

		return new Promise<boolean>((resolve) => {
			authState(this.auth)
				.pipe(takeUntil(resolved$))
				.subscribe(async (user) => {
					if (user === null) {
						resolve(true);
						resolved$.next(true);
						return;
					}

					if (!this.guestAuthService.hasCode()) {
						resolve(true);
						resolved$.next(true);
						return;
					}

					try {
						const tokenResult = await user.getIdTokenResult();
						const orgId =
							this.organizationId$.value ??
							(tokenResult.claims['orgId'] as string | undefined);
						if (!orgId) {
							resolve(true);
							resolved$.next(true);
							return;
						}

						await this.guestAuthService
							.setupGuestSession(orgId, user.uid)
							.then((sessionValid) => {
								resolve(!sessionValid);
								resolved$.next(true);
							});
					} catch {
						resolve(true);
						resolved$.next(true);
					}
				});
		})
			.catch(() => true)
			.then((allowAuth) => {
				if (!allowAuth) {
					this.router.navigate([successRoute]);
					return false;
				}
				return true;
			});
	}
}
